import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../utils/api';
import { ArrowLeft, Loader2 } from 'lucide-react';
import ReportView from '../components/ReportView';

export default function AnalysisReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);
  const [genStatus, setGenStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getMatches()
      .then((res) => {
        if (cancelled) return;
        const m = (res.matches || []).find((mm) => mm.id === id);
        if (!m) { navigate('/dashboard'); return; }
        setMatch(m);
        return api.getReportForMatch(id)
          .then((r) => { if (!cancelled) setReport(r.report); })
          .catch(() => { if (!cancelled) setReport(null); });
      })
      .catch(() => { if (!cancelled) navigate('/dashboard'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, navigate]);

  const pollingRef = React.useRef(false);
  useEffect(() => {
    if (loading || report || !match) return;
    if (pollingRef.current) return;
    let cancelled = false;
    pollingRef.current = true;
    (async () => {
      try {
        const initial = await api.getGenerationStatus(id).catch(() => null);
        if (cancelled) return;
        if (!initial) { pollingRef.current = false; return; }
        const initStatus = initial.status;
        if (initStatus === 'completed') {
          
          const r = await api.getReportForMatch(id).catch(() => null);
          if (!cancelled && r && r.report) { setReport(r.report); pollingRef.current = false; return; }
        }
        if (initStatus !== 'queued' && initStatus !== 'generating' && initStatus !== 'pending') {
          pollingRef.current = false; return;
        }
        setGenStatus(initStatus);
        const deadline = Date.now() + 90 * 1000;
        while (!cancelled && Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 3000));
          if (cancelled) break;
          let st = null;
          try { st = await api.getGenerationStatus(id); } catch (_) {}
          if (!st) continue;
          if (st.status) setGenStatus(st.status);
          if (st.status === 'completed') {
            const r = await api.getReportForMatch(id).catch(() => null);
            if (!cancelled && r && r.report) { setReport(r.report); break; }
          }
          if (st.status === 'failed') break;
        }
      } finally {
        if (!cancelled) pollingRef.current = false;
      }
    })();
    return () => { cancelled = true; };
  }, [loading, report, match, id]);

  // Retry generation on the EXISTING match. Never recreates the match or its
  // key moments - /api/analyze reads them from the server by matchId.
  const handleRetryGeneration = async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      const res = await api.retryAnalysis(id);
      let reportId = res && (res.reportId || res.report_id || (res.report && res.report.id));
      const inProgress = res && (res.__status === 202 || (res.status === 'generating' && !reportId));
      if (!reportId && inProgress) {
        const deadline = Date.now() + 90 * 1000;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 3000));
          let st = null;
          try { st = await api.getGenerationStatus(id); } catch (_) {}
          if (st && st.status) setGenStatus(st.status);
          if (st && (st.reportId || st.report_id)) { reportId = st.reportId || st.report_id; break; }
          if (st && st.status === 'completed') { break; }
          if (st && st.status === 'failed') { throw new Error('Generation failed again. Please review the match details and try once more.'); }
        }
      }
      if (reportId) {
        const r = await api.getReportForMatch(id).catch(() => null);
        if (r && r.report) { setReport(r.report); return; }
        window.location.reload();
      } else {
        setRetryError('Still generating - please wait a moment and retry.');
      }
    } catch (err) {
      setRetryError(err.message || 'Retry failed.');
    } finally {
      setRetrying(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center py-24">
      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
    </div>
  );
  if (!match) return null;
  if (!report) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <p className="text-navy-900 font-medium mb-2 flex items-center justify-center gap-2">
        {genStatus && (genStatus === 'queued' || genStatus === 'generating' || genStatus === 'pending') ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin text-navy-900" />
            Report is being generated ({genStatus})...
          </>
        ) : (
          'No report generated yet for this match.'
        )}
      </p>
      <p className="text-gray-500 text-sm mb-6">
        Your match and key moments are saved. You can generate the coaching report for this match now.
      </p>
      <button
        type="button"
        onClick={handleRetryGeneration}
        disabled={retrying}
        className="inline-flex items-center gap-2 bg-navy-900 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-navy-800 disabled:opacity-60"
      >
        {retrying ? (<><Loader2 className="w-4 h-4 animate-spin" /> Generating{genStatus ? ` (${genStatus})` : ''}…</>) : 'Retry generation'}
      </button>
      {retryError && <p className="text-red-600 text-sm mt-4">{retryError}</p>}
      <div className="mt-6">
        <Link to={`/matches`} className="text-navy-600 hover:underline text-sm">Back to matches</Link>
      </div>
    </div>
  );

  const externalVideoUrl = report.reference_video_url || report.referenceVideoUrl || match.original_submitted_video_url || match.original_video_url || match.external_video_url;

  const handleCopy = () => {
    const teamName = match.our_team_name || match.team_name || 'Team';
    const opponent = match.opponent_name || match.opponent || 'Opponent';
    const txt = [
      `TACTMARK ANALYSIS REPORT`,
      `${teamName} vs ${opponent}`,
      [match.match_date ? `Date: ${match.match_date}` : '', match.final_score ? `Score: ${match.final_score}` : ''].filter(Boolean).join(' | '),
      ``,
      `EXECUTIVE VERDICT`,
      report.verdict_summary || report.executiveVerdict || report.executive_verdict || '',
      ``,
      `WHAT DECIDED THE MATCH`,
      report.what_decided_match || report.whatDecidedTheMatch || '',
      ``,
      `DECISIVE MOMENTS`,
      (Array.isArray(report.decisive_moments) ? report.decisive_moments : [])
        .map((m, i) => typeof m === 'string' ? `- ${m}` : `${m.minute || m.minute ? "'" : ''} ${m.whatHappened || m.what_happened || m.event || ''}`)
        .join('\n'),
      ``,
      `TRAINING PRIORITY`,
      (Array.isArray(report.training_focus) ? report.training_focus : (Array.isArray(report.training_focus_blocks) ? report.training_focus_blocks : []))
        .map((t) => typeof t === 'string' ? `- ${t}` : `- ${t.area || t.title || ''}: ${t.drill || t.detail || ''}`)
        .join('\n'),
    ].join('\n');
    navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };


  const handleShare = async () => {
    if (!report) return;
    setSharing(true);
    try {
      const data = await api.createShareToken(report.id || report.report_id);
      if (data.token) {
        const url = data.url || `${window.location.origin}/share/${data.token}`;
        setShareUrl(url);
        navigator.clipboard.writeText(url).catch(() => {});
      }
    } catch (e) {
      console.error('Share failed', e);
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back button */}
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-navy-900 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>

      {shareUrl && (
        <div className="mb-4 flex items-center gap-2 bg-navy-50 border border-navy-200 rounded-lg px-4 py-2 text-sm">
          <span className="text-navy-700 truncate flex-1">{shareUrl}</span>
          <button onClick={() => navigator.clipboard.writeText(shareUrl)} className="text-navy-600 hover:text-navy-900 text-xs font-medium">Copy</button>
        </div>
      )}
      <ReportView
        match={match}
        report={report}
        externalVideoUrl={externalVideoUrl}
        showActions={true}
        onCopy={handleCopy}
        copied={copied}
        onShare={handleShare}
        sharing={sharing}
        shareUrl={shareUrl}
      />
    </div>
  );
}
